import { useLayoutEffect, useRef, useState } from "react";
import { usePlatformConfig } from "../utils/platformConfig";
import { liveServices } from "../utils/services";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { getAuth } from "../utils/auth";

/* A tab's word, sized to its tab. A single word wider than the tab ("ஓட்டுநர்கள்",
   "டாஷ்போர்டு") is set a little smaller until it fits, instead of running over the
   next tab; a label of two words still wraps onto two lines, and a word that fits keeps
   its 11px. Keyed by the word, so a new language starts again at 11px. */
function TabLabel({ text, color }) {
  const ref = useRef(null);
  const [size, setSize] = useState(11);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && size > 8.5 && el.scrollWidth > el.clientWidth + 1) setSize((s) => s - 0.5);
  }, [size]);
  return (
    <span ref={ref} style={{ fontSize: `${size}px`, fontWeight: 600, color, letterSpacing: "-0.1px", overflowWrap: size <= 8.5 ? "anywhere" : "normal" }}
      className="leading-none text-center w-full">
      {text}
    </span>
  );
}

/* The nav stays put. It used to slide away on every scroll down and return on scroll
   up, so on a long list it was usually gone - or moving - exactly when someone wanted
   to switch tabs. It is fixed to the bottom edge now, always visible. */

// Taxi cab SVG
const TaxiIcon = ({ active }) => (
  <svg width="18" height="14" viewBox="0 0 34 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="10" width="32" height="9" rx="2" fill={active ? "var(--color-primary)" : "#9CA3AF"} />
    <path d="M8 10 L11 4 L23 4 L26 10Z" fill={active ? "var(--color-primary)" : "#9CA3AF"} />
    <rect x="13" y="2" width="8" height="3" rx="1" fill={active ? "#fff" : "#E5E7EB"} />
    <text x="17" y="4.7" textAnchor="middle" fontSize="2.2" fontWeight="bold" fill={active ? "var(--color-primary)" : "#6B7280"}>TAXI</text>
    <circle cx="25" cy="19" r="3" fill="#fff" stroke={active ? "var(--color-primary)" : "#6B7280"} strokeWidth="1.5"/>
    <circle cx="25" cy="19" r="1.2" fill={active ? "var(--color-primary)" : "#6B7280"}/>
    <circle cx="9" cy="19" r="3" fill="#fff" stroke={active ? "var(--color-primary)" : "#6B7280"} strokeWidth="1.5"/>
    <circle cx="9" cy="19" r="1.2" fill={active ? "var(--color-primary)" : "#6B7280"}/>
    <path d="M11 9.5 L13 5.5 L21 5.5 L23 9.5Z" fill={active ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.25)"} />
  </svg>
);

// Driver icon
const DriverIcon = ({ active }) => {
  const c = active ? "var(--color-primary)" : "#6B7280";
  return (
    <svg width="18" height="18" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="40" cy="16" r="10" stroke={c} strokeWidth="2.2" fill="none"/>
      <line x1="36" y1="26" x2="35" y2="31" stroke={c} strokeWidth="2" strokeLinecap="round"/>
      <line x1="44" y1="26" x2="45" y2="31" stroke={c} strokeWidth="2" strokeLinecap="round"/>
      <path d="M35 31 Q20 34 14 46" stroke={c} strokeWidth="2.2" strokeLinecap="round" fill="none"/>
      <path d="M45 31 Q60 34 66 46" stroke={c} strokeWidth="2.2" strokeLinecap="round" fill="none"/>
      <path d="M35 31 L40 38" stroke={c} strokeWidth="2" strokeLinecap="round"/>
      <path d="M45 31 L40 38" stroke={c} strokeWidth="2" strokeLinecap="round"/>
      <path d="M14 46 Q20 56 40 56 Q60 56 66 46" stroke={c} strokeWidth="2.2" strokeLinecap="round" fill="none"/>
      <path d="M18 44 Q16 54 22 60" stroke={c} strokeWidth="2.2" strokeLinecap="round" fill="none"/>
      <path d="M62 44 Q64 54 58 60" stroke={c} strokeWidth="2.2" strokeLinecap="round" fill="none"/>
      <circle cx="40" cy="66" r="16" stroke={c} strokeWidth="2.2" fill="none"/>
      <circle cx="40" cy="66" r="6" stroke={c} strokeWidth="2" fill="none"/>
      <circle cx="40" cy="66" r="2" fill={c}/>
      <line x1="40" y1="60" x2="40" y2="50" stroke={c} strokeWidth="2" strokeLinecap="round"/>
      <line x1="34.8" y1="70.2" x2="27" y2="76" stroke={c} strokeWidth="2" strokeLinecap="round"/>
      <line x1="45.2" y1="70.2" x2="53" y2="76" stroke={c} strokeWidth="2" strokeLinecap="round"/>
      <circle cx="23" cy="61" r="3" stroke={c} strokeWidth="2" fill="none"/>
      <circle cx="57" cy="61" r="3" stroke={c} strokeWidth="2" fill="none"/>
    </svg>
  );
};

const NAV_ITEMS = [
  { to: "/", labelKey: "navCars", label: "Cars",    icon: (a) => <TaxiIcon active={a} /> },
  // A short label of its own: "hireDriver" is a sentence in some languages
  // (డ్రైవర్‌ను నియమించుకోండి) and wrapped onto the icon beside it.
  { to: "/drivers", labelKey: "navHireDriver", label: "Drivers", icon: (a) => <DriverIcon active={a} /> },
  {
    /* The customer's slot is their history — enquiries and past trips, not bookings;
       nothing on this platform is a confirmed reservation. The same slot is a cab
       owner's My Cars and a hire driver's Dashboard, so only this label changed. */
    to: "/bookings", label: "History", labelKey: "bookings",
    icon: (a) => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
        stroke={a ? "var(--color-primary)" : "#6B7280"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="5" width="18" height="16" rx="2"/><path d="M8 3v4M16 3v4M3 9h18"/>
      </svg>
    )
  },
  {
    to: "/services", label: "Services", labelKey: "services",
    icon: (a) => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
        stroke={a ? "var(--color-primary)" : "#6B7280"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/>
        <rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/>
      </svg>
    )
  },
  {
    to: "/account", label: "Account", labelKey: "account",
    icon: (a) => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
        stroke={a ? "var(--color-primary)" : "#6B7280"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="8" r="4"/><path d="M6 20c1.5-3 4-4 6-4s4.5 1 6 4"/>
      </svg>
    )
  }
];

export default function BottomNav() {
  const { pathname } = useLocation();
  // Services shows only where a service has a business (utils/services, mock Fix_D change 13).
  const hasServices = liveServices(usePlatformConfig()).length > 0;
  const { t, lang } = useLanguage();
  const auth = getAuth();
  const role = (auth?.role || "").toUpperCase();
  // Canonical role is ALTING_DRIVER; ALT_DRIVER is a legacy alias. Match both so the
  // hire-driver overrides actually fire (they never did while only ALT_DRIVER was checked).
  const isHireDriver = role === "ALTING_DRIVER" || role === "ALT_DRIVER";

  return (
    <>
      {/* Platform Disclaimer Footer */}
      <div className="bottom-nav fixed left-0 right-0 z-30 pointer-events-none"
        style={{ bottom: "calc(0.5rem + env(safe-area-inset-bottom, 0px))" }}>
        <p className="text-center w-full px-4"
          style={{ fontSize: "var(--token-text-label)", color: "#98A2B3", lineHeight: 1.5 }}>
          {t("platformDisclaimer")}
        </p>
      </div>

      <nav className="bottom-nav fixed z-40 flex items-center justify-around"
        style={{
          /* Anchored to the bottom edge, full width, rounded only on top. It used to
             float 6px up as a rounded pill with a gap either side, and the list
             scrolled visibly behind and around it - a Call button showing through
             beside "Account" looked like part of the bar. The home-indicator inset
             is now padding inside the bar rather than a gap under it. */
          bottom: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: "100%",
          maxWidth: "100%",
          height: "calc(58px + env(safe-area-inset-bottom, 0px))",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
          paddingLeft: "6px",
          paddingRight: "6px",
          background: "#FFFFFF",
          borderRadius: "20px 20px 0 0",
          borderTop: "1px solid #EEF1F4",
          boxShadow: "0 -4px 18px rgba(15,23,42,0.06)"
        }}>
        {NAV_ITEMS.filter((item) => item.to !== "/services" || hasServices || pathname === "/services").map((item) => {
          const isActive = pathname === item.to;
          let displayLabel = item.labelKey ? t(item.labelKey) : item.label;
          // Role-aware label overrides for the Bookings slot.
          if (item.to === "/bookings" && (role === "CAB_OWNER" || role === "OWNER")) displayLabel = t("ac_myCars");
          if (item.to === "/bookings" && isHireDriver) displayLabel = t("navDashboard");
          // A hire driver's Bookings slot is their dashboard — show the driver glyph.
          const renderIcon = (item.to === "/bookings" && isHireDriver)
            ? ((a) => <DriverIcon active={a} />)
            : item.icon;
          return (
            <Link key={item.to} to={item.to}
              className="flex flex-col items-center justify-center gap-[2px] px-1 py-1 rounded-2xl transition-all"
              style={{
                color: isActive ? "var(--color-primary)" : "#7B8794",
                minWidth: 0, flex: "1 1 0", height: "100%"
              }}>
              <span className={`transition-transform duration-300 ${isActive ? "scale-110 -translate-y-[1px]" : ""}`}>
                {renderIcon(isActive)}
              </span>
              <TabLabel key={displayLabel} text={displayLabel} color={isActive ? "var(--color-primary)" : "#7B8794"} />
            </Link>
          );
        })}
      </nav>
    </>
  );
}
